/**
 * Bundle 39 - Global pricing audit page. Super-admin only.
 *
 * Chronological cross-event view of every pricing_tier_activated /
 * deactivated / modified audit entry within a rolling window. Used
 * for revenue-ops sweeps, chargeback dispute prep, and compliance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollText, Download } from 'lucide-react';
import api from '@/utils/api';

const ACTIONS = [
  { value: '', label: 'All actions' },
  { value: 'pricing_tier_activated', label: 'Activated' },
  { value: 'pricing_tier_deactivated', label: 'Deactivated' },
  { value: 'pricing_tier_modified', label: 'Modified' },
];

const ACTION_STYLE = {
  pricing_tier_activated: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  pricing_tier_deactivated: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  pricing_tier_modified: 'bg-amber-50 text-amber-800 border-amber-200',
};

const AdminPricingAuditPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    event_id: '', tier_name: '', action: '', days: 30,
  });
  const [meta, setMeta] = useState(null);

  const fetchEntries = React.useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.event_id) params.set('event_id', filters.event_id);
    if (filters.tier_name) params.set('tier_name', filters.tier_name);
    if (filters.action) params.set('action', filters.action);
    params.set('days', String(filters.days || 30));
    params.set('limit', '500');
    try {
      const { data } = await api.get(`/admin/pricing-audit/global?${params}`);
      setEntries(data.entries || []);
      setMeta({ since: data.since, count: data.count });
    } catch (e) {
      setEntries([]);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const exportCsv = () => {
    if (!entries.length) return;
    const rows = [[
      'created_at', 'action', 'event_slug', 'event_title', 'tier_name',
      'tier_price_cents', 'effective_from_utc', 'effective_until_utc',
      'actor_id',
    ]];
    entries.forEach((e) => {
      rows.push([
        e.created_at, e.action, e.event_slug || '', e.event_title || '',
        e.tier_name || '', e.tier_price_cents ?? '',
        e.effective_from_utc || '', e.effective_until_utc || '',
        e.actor_id || '',
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'pricing-audit-global.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const byEvent = useMemo(() => {
    const s = new Set();
    entries.forEach((e) => { if (e.event_slug) s.add(e.event_slug); });
    return Array.from(s);
  }, [entries]);

  return (
    <div className="p-6 max-w-6xl space-y-6" data-testid="admin-pricing-audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-primary-900 flex items-center gap-2">
            <ScrollText className="h-6 w-6" /> Pricing audit
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Every pricing tier activation, deactivation, and admin edit
            across every event. Super-admin only.
          </p>
        </div>
        {entries.length > 0 && (
          <Button
            variant="outline" onClick={exportCsv}
            data-testid="pricing-audit-global-export"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        )}
      </div>

      <Card className="border shadow-soft">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Filters</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-5">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Event ID</Label>
            <Input
              value={filters.event_id}
              onChange={(e) => setFilters({ ...filters, event_id: e.target.value })}
              placeholder="uuid"
              data-testid="pricing-audit-filter-event"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Tier name</Label>
            <Input
              value={filters.tier_name}
              onChange={(e) => setFilters({ ...filters, tier_name: e.target.value })}
              placeholder="e.g. Last Chance"
              data-testid="pricing-audit-filter-tier"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Action</Label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="w-full mt-1 border border-neutral-300 rounded-md px-2 py-1 text-sm"
              data-testid="pricing-audit-filter-action"
            >
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-secondary-700">Days</Label>
            <Input
              type="number" min="1" max="365"
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) || 30 })}
              data-testid="pricing-audit-filter-days"
              className="mt-1"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={fetchEntries}
              data-testid="pricing-audit-apply-filters"
              className="w-full bg-primary-800 hover:bg-primary-900 text-white"
            >
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-neutral-500" data-testid="pricing-audit-meta">
        {loading ? 'Loading...' : (meta
          ? `${meta.count} entries since ${meta.since} - ${byEvent.length} event(s)`
          : 'No data')}
      </div>

      <div className="space-y-2" data-testid="pricing-audit-list">
        {!loading && entries.length === 0 && (
          <div className="text-sm text-neutral-500 py-8 text-center border border-dashed border-neutral-200 rounded-xl">
            No pricing history in this window.
          </div>
        )}
        {entries.map((e) => (
          <div
            key={e.id}
            className="border border-neutral-200 rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-2"
            data-testid={`pricing-audit-global-row-${e.id}`}
          >
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md border font-semibold ${ACTION_STYLE[e.action] || ''}`}>
              {e.action.replace('pricing_tier_', '')}
            </span>
            <Link
              to={`/admin/events/${e.event_id}/edit`}
              className="text-secondary-700 hover:underline font-semibold"
            >
              {e.event_slug || e.event_id}
            </Link>
            <span className="text-primary-900 font-semibold">{e.tier_name || 'tier'}</span>
            {e.tier_price_cents != null && (
              <span className="text-neutral-700">${(e.tier_price_cents / 100).toFixed(2)}</span>
            )}
            <span className="ml-auto text-[10px] text-neutral-400 font-mono">
              {e.created_at}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPricingAuditPage;
